import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useDispatch, useSelector } from 'react-redux';
import { apiGetAutoPricingLimits, apiSaveAutoPricingLimits } from '@store';

const initialLimits = {
    transaction_fee: 0,
    clearing_fee: 0,
    settlement_fee: 0,
    initial_margin: 0,
    variation_margin: 0,
    service_fee: 0,
    balancing_fee: 0,
    set_margin: 0
};

export default function AutoPricingLimits() {
    const dispatch = useDispatch();
    const { isGetAutoPricingLimitsLoading, isSaveAutoPricingLimitsLoading } =
        useSelector((state) => state.eex);

    const [state, setState] = useState(initialLimits);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');

    useEffect(() => {
        (async () => {
            const resp = await dispatch(apiGetAutoPricingLimits());
            if (apiGetAutoPricingLimits.fulfilled.match(resp)) {
                const data = resp.payload?.data || {};
                setState({
                    transaction_fee: data.transaction_fee || 0,
                    clearing_fee: data.clearing_fee || 0,
                    settlement_fee: data.settlement_fee || 0,
                    initial_margin: data.initial_margin || 0,
                    variation_margin: data.variation_margin || 0,
                    service_fee: data.service_fee || 0,
                    balancing_fee: data.balancing_fee || 0,
                    set_margin: data.set_margin || 0
                });
            }
        })();
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) {
            return;
        }
        setState((prev) => ({ ...prev, [name]: value }));
        setStatusMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            transaction_fee: parseFloat(state.transaction_fee) || 0,
            clearing_fee: parseFloat(state.clearing_fee) || 0,
            settlement_fee: parseFloat(state.settlement_fee) || 0,
            initial_margin: parseFloat(state.initial_margin) || 0,
            variation_margin: parseFloat(state.variation_margin) || 0,
            service_fee: parseFloat(state.service_fee) || 0,
            balancing_fee: parseFloat(state.balancing_fee) || 0,
            set_margin: parseFloat(state.set_margin) || 0
        };

        const resp = await dispatch(apiSaveAutoPricingLimits(payload));

        if (apiSaveAutoPricingLimits.fulfilled.match(resp)) {
            setStatusMessage('Auto pricing limits saved successfully!');
            setStatusType('success');
        } else {
            setStatusMessage(
                resp.payload?.message ||
                    resp.payload?.error ||
                    'Unable to save auto pricing limits. Please try again.'
            );
            setStatusType('error');
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                p: 3,
                maxWidth: '100%',
                width: '100%',

                borderRadius: 2
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background:
                            'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Auto Pricing Margins
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {/* Fees */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Fees
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="transactionFeeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Transaction Fee
                            </InputLabel>
                            <OutlinedInput
                                id="transactionFeeLabel"
                                label="Transaction Fee"
                                name="transaction_fee"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.transaction_fee}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="clearingFeeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Clearing Fee
                            </InputLabel>
                            <OutlinedInput
                                id="clearingFeeLabel"
                                label="Clearing Fee"
                                name="clearing_fee"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.clearing_fee}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="settlementFeeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Settlement Fee
                            </InputLabel>
                            <OutlinedInput
                                id="settlementFeeLabel"
                                label="Settlement Fee"
                                name="settlement_fee"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.settlement_fee}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Margins */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Margins
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="initialMarginLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Initial Margin
                            </InputLabel>
                            <OutlinedInput
                                id="initialMarginLabel"
                                label="Initial Margin"
                                name="initial_margin"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.initial_margin}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="variationMarginLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Variation Margin
                            </InputLabel>
                            <OutlinedInput
                                id="variationMarginLabel"
                                label="Variation Margin"
                                name="variation_margin"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.variation_margin}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="setMarginLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                SET Margin
                            </InputLabel>
                            <OutlinedInput
                                id="setMarginLabel"
                                label="SET Margin"
                                name="set_margin"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.set_margin}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Other Fees */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Other Fees
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="serviceFeeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Service Fee
                            </InputLabel>
                            <OutlinedInput
                                id="serviceFeeLabel"
                                label="Service Fee"
                                name="service_fee"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.service_fee}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="balancingFeeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Balancing Fee
                            </InputLabel>
                            <OutlinedInput
                                id="balancingFeeLabel"
                                label="Balancing Fee"
                                name="balancing_fee"
                                type="number"
                                inputProps={{ min: 0, step: 0.001 }}
                                onChange={handleChange}
                                value={state.balancing_fee}
                            />
                        </FormControl>
                    </Box>
                </Box>
            </Box>

            {statusMessage && (
                <Alert
                    severity={statusType}
                    icon={
                        statusType === 'success' ? (
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                            <ErrorIcon sx={{ color: '#F44336' }} />
                        )
                    }
                    sx={{
                        mt: 2,
                        backgroundColor:
                            statusType === 'success' ? '#E8F5E9' : '#FFEBEE',
                        color: statusType === 'success' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${
                            statusType === 'success' ? '#C8E6C9' : '#FFCDD2'
                        }`
                    }}
                >
                    {statusMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={
                        isGetAutoPricingLimitsLoading ||
                        isSaveAutoPricingLimitsLoading
                    }
                    sx={{
                        backgroundColor: '#0b9cfd',
                        color: '#fff',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(11, 156, 253, 0.3)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(11, 156, 253, 0.4)'
                        },
                        '&:disabled': {
                            background: '#CCCCCC',
                            boxShadow: 'none'
                        },
                        marginLeft: 'auto'
                    }}
                >
                    {isSaveAutoPricingLimitsLoading
                        ? 'Saving...'
                        : 'Save Limits'}
                </Button>
            </Box>
        </Box>
    );
}
